/**
 * Platform Usage Widget (issue #1654, part of epic #1650)
 *
 * Shows EA Modeling application usage telemetry (active users over the
 * trailing 30 days, top routes, top features) — reusing the existing
 * `UsageRollupRepository` rollup (epic #1448) rather than a new
 * aggregation. This deviates from `homepage-dashboard-spec.md`'s original
 * "Platform Product Usage" definition (services using low-code platform
 * technologies); see `backend/src/graphql/resolvers/platformUsage.ts` and
 * [ADR-1654](../../../../documents/decisions/1654-platform-usage-widget-reuses-telemetry-rollup.md)
 * for why. Administrator or `analytics:read` role required. Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import {
  usePlatformUsage,
  type PlatformUsageKeyCount,
  type PlatformUsageTimeSeriesPoint,
} from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './PlatformUsageWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'platform-usage',
  title: 'Platform Usage',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface PlatformUsageStats {
  periodStart: string;
  periodEnd: string;
  activeUsers: PlatformUsageTimeSeriesPoint[];
  topRoutes: PlatformUsageKeyCount[];
  topFeatures: PlatformUsageKeyCount[];
}

type PlatformUsageQueryData = ReturnType<typeof usePlatformUsage>['data'];

function extractPlatformUsageStats(data: PlatformUsageQueryData): PlatformUsageStats | null {
  if (!data?.platformUsage) {
    return null;
  }
  return data.platformUsage;
}

function totalActiveUsers(activeUsers: PlatformUsageTimeSeriesPoint[]): number {
  return activeUsers.reduce((sum, point) => sum + point.value, 0);
}

export function PlatformUsageWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    usePlatformUsage(),
    extractPlatformUsageStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="platform-usage-widget__empty">
          <IconElement name="insights" className="platform-usage-widget__empty-icon" />
          <p className="forge-typography--body2">No platform usage data available</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <div className="platform-usage-widget__content">
        <div className="platform-usage-widget__summary-tile" data-testid="platform-usage-summary">
          <div className="platform-usage-widget__summary-count">
            {totalActiveUsers(stats.activeUsers)}
          </div>
          <div className="platform-usage-widget__summary-label forge-typography--label2">
            Active users (last 30 days)
          </div>
        </div>

        <div className="platform-usage-widget__section">
          <h4 className="platform-usage-widget__section-title forge-typography--label2">
            Top routes
          </h4>
          {stats.topRoutes.length === 0 ? (
            <p className="platform-usage-widget__section-empty forge-typography--caption">
              No route activity in this window
            </p>
          ) : (
            <ul
              className="platform-usage-widget__key-count-list"
              data-testid="platform-usage-top-routes"
            >
              {stats.topRoutes.map((entry) => (
                <li key={entry.name} className="platform-usage-widget__key-count-row">
                  <span className="platform-usage-widget__key-count-name">{entry.name}</span>
                  <span className="platform-usage-widget__key-count-value">{entry.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="platform-usage-widget__section">
          <h4 className="platform-usage-widget__section-title forge-typography--label2">
            Top features
          </h4>
          {stats.topFeatures.length === 0 ? (
            <p className="platform-usage-widget__section-empty forge-typography--caption">
              No feature activity in this window
            </p>
          ) : (
            <ul
              className="platform-usage-widget__key-count-list"
              data-testid="platform-usage-top-features"
            >
              {stats.topFeatures.map((entry) => (
                <li key={entry.name} className="platform-usage-widget__key-count-row">
                  <span className="platform-usage-widget__key-count-name">{entry.name}</span>
                  <span className="platform-usage-widget__key-count-value">{entry.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </DashboardWidget>
  );
}
